// S-1 — private client folders: the UI pieces My Storage (index.html renderSpecStorage) uses for the managed folders.
// Exposed as window.HUD_CLIENT_STORAGE. The rules live in model.js (pure); the server (S-1 M) is the authority.
import * as M from './model.js';

const H = () => window.HUD || {};
const T = (k, p) => H().T?.(k, p) ?? k;
// F-19's read-only helpers, from index.html via the bridge (no local copy).
const _paidFn = (feature, fn) => H()._paidFn(feature, fn);
const _paidMark = (btn) => H()._paidMark(btn);
const _readOnlyBanner = (endedText, neverText, feature) => H()._readOnlyBanner(endedText, neverText, feature);

// The Clients root's "people folder" icon (mock change 3: a distinct icon, never a lock).
const PEOPLE_FOLDER_SVG = '<svg viewBox="0 0 28 24" width="1.15em" height="1.15em" aria-hidden="true" style="vertical-align:-0.15em"><path d="M2 5a2 2 0 0 1 2-2h6l2 2.5h12a2 2 0 0 1 2 2V19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z" fill="#fcd34d"/><circle cx="11.5" cy="11.5" r="2.3" fill="#0d6b63"/><circle cx="17.5" cy="11.5" r="2.3" fill="#0d6b63"/><path d="M7.5 18.5c.6-2.2 2.2-3.4 4-3.4s3.4 1.2 4 3.4M13.5 18.5c.6-2.2 2.2-3.4 4-3.4s3.4 1.2 4 3.4" fill="none" stroke="#0d6b63" stroke-width="1.6" stroke-linecap="round"/></svg>';

export function folderIconEl(folder) {
  const span = document.createElement('span');
  span.className = 'cs-ficon';
  if (folder?.kind === 'clients_root') { span.innerHTML = PEOPLE_FOLDER_SVG; span.dataset.kind = 'clients_root'; }
  else span.textContent = '📁';
  return span;
}

// The label a folder shows: the Clients root in the app language; a client folder with its QF-8 suffix.
export function folderLabel(folder, names) {
  if (folder?.kind === 'clients_root') return T('storage_clients_folder');
  return (names && names.get(folder.id)) || folder.name;
}

export function formerBadgeEl(folder) {
  if (!M.isFormer(folder)) return null;
  const { el } = H();
  return el('span', { class: 'cs-former', style: { display: 'inline-block', fontSize: '.68rem', fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: '999px', padding: '2px 8px', marginInlineStart: '6px', whiteSpace: 'nowrap', verticalAlign: 'middle' } }, [T('storage_former_client')]);
}

// The date of birth the QF-8 suffix uses, from the client the folder is linked to.
function dobOf(folder) {
  const DB = H().DB || {};
  if (folder.child_id) return (DB.children || []).find(c => c.id === folder.child_id)?.dob || null;
  if (folder.specialist_patient_id) return (DB.specialistPatients || []).find(p => p.id === folder.specialist_patient_id)?.dob || null;
  return null;
}

// The folders of one level, in display order, with their labels. Root: the Clients folder first. Inside Clients:
// current clients by name, then former clients (QF-8 suffixes applied).
export function prepareLevel(folders, currentFolder) {
  const list = folders || [];
  if (currentFolder?.kind === 'clients_root') {
    const clients = list.filter(f => f.kind === 'client');
    const names = M.displayNames(clients, dobOf);
    const sorted = M.sortClientFolders(clients, names);
    const others = list.filter(f => f.kind !== 'client');
    return [...sorted, ...others].map(f => ({ folder: f, label: folderLabel(f, names) }));
  }
  const root = list.filter(f => f.kind === 'clients_root');
  return [...root, ...list.filter(f => f.kind !== 'clients_root')].map(f => ({ folder: f, label: folderLabel(f) }));
}

// The info toast for a refused rename / move / delete of a managed folder. Returns true when it refused.
export function refuse(folder, action) {
  const k = M.lockReason(folder, action);
  if (!k) return false;
  H().toast?.(T(k), 'info', 6000);
  return true;
}

// A long-press on a folder: ordinary folders get the existing menu; a former client's folder offers delete only
// (with its own confirm); any other managed folder explains why it can't be changed.
export function folderPress(folder, openDefaultMenu, onDeleted) {
  if (!M.isManaged(folder)) return openDefaultMenu();
  if (M.isFormer(folder)) {
    const { openModal, openConfirm, mkBtn, toast } = H();
    openModal('📁 ' + folder.name, (mb, close) => {
      mb.appendChild(mkBtn('🗑️ ' + T('btn_delete'), 'btn-md btn-danger btn-full', () => {
        close();
        const k = M.deleteConfirmKeys(folder);
        openConfirm(T(k.title), T(k.msg), true, async () => {
          try { await deleteClientTree(folder); toast?.(T('storage_folder_deleted')); onDeleted?.(); }
          catch (e) { console.error('❌ delete former client folder:', e); toast?.(T('storage_upload_failed'), 'error'); }
        });
      }));
    }, 300);
    return;
  }
  refuse(folder, 'rename');
}

// Deletes a former client's folder with EVERYTHING in it: every object under client/<folder>/ and every file row of
// the tree (subfolders cascade in the DB; their file rows would otherwise fall back to the My Storage root).
export async function deleteClientTree(folder) {
  if (!M.isFormer(folder)) throw new Error('only a former client folder can be deleted');
  const supa = H()._supa;
  const prefix = M.CLIENT_PREFIX + folder.id + '/';
  const { data: rows, error: e1 } = await supa.from('spec_vault_files').select('id,storage_path').like('storage_path', prefix + '%');
  if (e1) throw e1;
  const paths = (rows || []).map(r => r.storage_path).filter(p => p.startsWith(prefix));
  for (let i = 0; i < paths.length; i += 100) {
    const { error } = await supa.storage.from('specialist-storage').remove(paths.slice(i, i + 100));
    if (error) throw error;
  }
  if (rows?.length) {
    const { error: e2 } = await supa.from('spec_vault_files').delete().in('id', rows.map(r => r.id));
    if (e2) throw e2;
  }
  const { data: gone, error: e3 } = await supa.from('spec_vault_folders').delete().eq('id', folder.id).select('id');
  if (e3) throw e3;
  if (!gone?.length) throw new Error('folder delete blocked');
}

// The client tree a write lands in (null = not in a client tree). Asked from the server (vault_client_root_of), so a
// breadcrumb reached through search can't produce a wrong object path.
export async function treeIdFor(folderId) {
  if (!folderId) return null;
  const { data, error } = await H()._supa.rpc('vault_client_root_of', { p_folder: folderId });
  if (error) throw error;
  return data || null;
}

// S4: the account-deletion warning's folder list (M.deleteWarningList). Two owner-scoped reads: my client folders
// (current and former) and the storage paths of my files under client/. Throws on a failed read; the caller decides.
export async function deleteWarningSummary() {
  const supa = H()._supa, specId = H().session?.id;
  if (!specId) throw new Error('no session');
  const [fo, fi] = await Promise.all([
    supa.from('spec_vault_folders').select('id,name,kind,former_at,child_id,specialist_patient_id,created_at').eq('specialist_id', specId).eq('kind', 'client'),
    supa.from('spec_vault_files').select('storage_path').eq('specialist_id', specId).like('storage_path', M.CLIENT_PREFIX + '%'),
  ]);
  if (fo.error) throw fo.error;
  if (fi.error) throw fi.error;
  const folders = fo.data || [];
  return M.deleteWarningList(folders, (fi.data || []).map(r => r.storage_path), M.displayNames(folders, dobOf));
}

// ── S2: the client's Files tab, second door ────────────────────────────────────────────────────────────────────
// "Private, only you": the same folder as My Storage › Clients › <client>, browsed in place (one folder, two doors).
// Parents never reach it (the panel is specialist-only, and RLS limits spec_vault_* to the owner).
const ONLY_YOU_SVG = '<svg viewBox="0 0 24 24" width="1.1em" height="1.1em" aria-hidden="true" style="vertical-align:-0.15em"><circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" stroke-width="2"/><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

const ctxKey = (ctx) => ctx ? (ctx.kind === 'child' ? 'c:' + ctx.child_id : 'p:' + ctx.specialist_patient_id) : '';

export function renderPrivatePanel(ctx) {
  const { el, mkBtn, S, session, _supa, toast, openModal, openConfirm, re, _safeStorageName, _formatBytes } = H();
  const specId = session?.id;
  const panel = el('div', { class: 'cs-private', style: { marginTop: '22px', padding: '14px', border: '1.5px solid var(--mint-l)', borderRadius: '14px', background: '#fff' } });
  const head = el('div', { class: 'cs-private-title', style: { display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 800, color: 'var(--navy)', fontSize: '.92rem' } });
  const icon = document.createElement('span'); icon.innerHTML = ONLY_YOU_SVG; icon.style.color = 'var(--teal)'; icon.style.display = 'inline-flex';
  head.appendChild(icon); head.appendChild(el('span', {}, [T('files_private_title')]));
  panel.appendChild(head);
  panel.appendChild(el('div', { class: 'cs-private-hint', style: { fontSize: '.76rem', color: 'var(--slate)', margin: '2px 0 12px' } }, [T('files_private_hint')]));
  // F-19 (QP-6, QR-b A): without the plan the folder stays readable (open, download, delete); writes pause → upgrade.
  const roBanner = _readOnlyBanner(T('storage_readonly_banner'), T('files_private_locked_hint'), 'Storage');
  if (roBanner) panel.appendChild(roBanner);
  const body = el('div', { class: 'cs-private-body' }, [el('div', { style: { color: 'var(--slate)', fontSize: '.8rem', padding: '8px 0' } }, [T('btn_loading')])]);
  panel.appendChild(body);
  if (!ctx || !specId) return panel;

  // navigation inside the panel: per client, kept across re-renders
  const key = ctxKey(ctx);
  if (!S._csPrivate || S._csPrivate.key !== key) S._csPrivate = { key, path: [] };
  const nav = S._csPrivate;

  (async () => {
    try {
      const { data: all, error } = await _supa.from('spec_vault_folders').select('*').eq('specialist_id', specId).eq('kind', 'client');
      if (error) throw error;
      const linked = ctx.kind === 'child' ? (H().DB?.specialistPatients || []).filter(p => p.linked_child_id === ctx.child_id).map(p => p.id) : [];
      const mine = M.clientFoldersFor(ctx, all || [], linked);
      body.innerHTML = '';
      if (!mine.length) { body.appendChild(el('div', { style: { color: 'var(--slate)', fontSize: '.8rem' } }, [T('storage_folder_empty')])); return; }
      const names = M.displayNames(mine, () => null);
      // one client folder: open it directly; two (a merged client, QS1M-2 A): list both first
      const top = mine.length === 1 ? { id: mine[0].id, name: names.get(mine[0].id) } : null;
      const path = top ? [top, ...nav.path] : nav.path;
      const cur = path.length ? path[path.length - 1] : null;
      const tree = path.length ? path[0].id : null;

      // breadcrumb: My Storage › Clients › <client> › … (the panel's own levels are clickable)
      const bc = el('div', { class: 'cs-crumbs', style: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', fontSize: '.78rem', marginBottom: '10px', color: 'var(--slate)' } });
      const crumb = (txt, onclick, last) => { const s = el('span', { dir: 'auto', style: { fontWeight: last ? 700 : 500, color: last ? 'var(--navy)' : onclick ? 'var(--teal)' : 'var(--slate)', cursor: onclick ? 'pointer' : 'default' } }, [txt]); if (onclick) s.onclick = onclick; return s; };
      const sep = () => el('span', { style: { color: 'var(--slate-l)' } }, ['›']);
      bc.appendChild(crumb(T('storage_title'))); bc.appendChild(sep()); bc.appendChild(crumb(T('storage_clients_folder')));
      path.forEach((p, i) => { bc.appendChild(sep()); bc.appendChild(crumb(p.name, i < path.length - 1 ? () => { nav.path = nav.path.slice(0, top ? i : i + 1); re(); } : null, i === path.length - 1)); });
      body.appendChild(bc);

      // toolbar (New Folder, Upload; "Move to…" comes with S3). Wraps cleanly at 390.
      if (cur) {
        const tb = el('div', { class: 'cs-toolbar', style: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' } });
        tb.appendChild(_paidMark(mkBtn('📁 ' + T('storage_new_folder'), 'btn-sm btn-secondary', _paidFn('Storage', () => {
          openModal('📁 ' + T('storage_new_folder'), (mb, close) => {
            mb.appendChild(el('div', { class: 'inp-label' }, [T('storage_folder_name')]));
            const inp = el('input', { class: 'inp', placeholder: T('storage_folder_name'), style: { marginBottom: '14px' } });
            mb.appendChild(inp);
            mb.appendChild(mkBtn(T('btn_save'), 'btn-md btn-primary btn-full', async () => {
              if (!inp.value.trim()) { toast(T('files_enter_name'), 'error'); return; }
              const { error: e } = await _supa.from('spec_vault_folders').insert({ specialist_id: specId, parent_folder_id: cur.id, name: inp.value.trim() }).select().single();
              if (e) { console.error('❌ private folder create:', e); toast(T('storage_upload_failed'), 'error'); return; }
              close(); toast(T('storage_folder_created')); re();
            }));
          }, 360);
        }))));
        tb.appendChild(_paidMark(mkBtn('📎 ' + T('storage_upload'), 'btn-sm btn-primary', _paidFn('Storage', () => {
          const inp = el('input', { type: 'file', multiple: true, style: { display: 'none' } });
          inp.onchange = async (ev) => {
            const files = [...ev.target.files]; if (!files.length) return;
            // spec ruling 7 / QF-4: the same quota as My Storage (STORAGE_LIMITS by tier, storage_limit_reached), on the
            // therapist's whole storage total, read fresh for the batch (the same query My Storage keeps it with)
            const { STORAGE_LIMITS } = H();
            const limitBytes = STORAGE_LIMITS[session?.storageTier || 'base'] || STORAGE_LIMITS.base;
            const { data: allF, error: qe } = await _supa.from('spec_vault_files').select('size_bytes').eq('specialist_id', specId);
            if (qe) { console.error('❌ private upload: storage total:', qe); toast(T('storage_upload_failed'), 'error'); return; }
            S._storageUsedBytes = (allF || []).reduce((s, r) => s + (r.size_bytes || 0), 0);
            for (const f of files) {
              if (S._storageUsedBytes + f.size > limitBytes) { toast(T('storage_limit_reached'), 'error'); break; }
              toast(T('storage_uploading'), 'info', 2000);
              const p2 = M.objectPath({ specId, treeId: tree, name: f.name, safeName: _safeStorageName });
              const { error: ue } = await _supa.storage.from('specialist-storage').upload(p2, f, { contentType: f.type, upsert: false });
              if (ue) { console.error('❌ private upload:', ue); toast(T('storage_upload_failed'), 'error'); continue; }
              const { error: de } = await _supa.from('spec_vault_files').insert({ specialist_id: specId, folder_id: cur.id, name: f.name, file_type: f.name.split('.').pop() || 'file', size_bytes: f.size, storage_path: p2 });
              if (de) { console.error('❌ private file row:', de); toast(T('storage_upload_failed'), 'error'); continue; }
              S._storageUsedBytes += f.size;
              toast(T('storage_uploaded'));
            }
            re();
          };
          document.body.appendChild(inp); inp.click(); inp.remove();
        }))));
        body.appendChild(tb);
      }

      // contents: the two client folders (merged client), or the current folder's subfolders + files
      const list = el('div', { class: 'cs-list' });
      const row = (iconTxt, label, meta, onclick) => {
        const r = el('div', { class: 'card hov cs-row', style: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', marginBottom: '6px', cursor: onclick ? 'pointer' : 'default' } });
        r.appendChild(el('span', { style: { fontSize: '1.2rem', flexShrink: 0 } }, [iconTxt]));
        r.appendChild(el('span', { dir: 'auto', style: { flex: 1, minWidth: 0, fontWeight: 600, fontSize: '.84rem', color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, [label]));
        if (meta) r.appendChild(el('span', { dir: 'ltr', style: { fontSize: '.72rem', color: 'var(--slate)', flexShrink: 0 } }, [meta]));
        if (onclick) r.onclick = onclick;
        return r;
      };
      if (!cur) {
        mine.forEach(f => list.appendChild(row('📁', names.get(f.id), null, () => { nav.path = [{ id: f.id, name: names.get(f.id) }]; re(); })));
      } else {
        const [{ data: subs, error: e1 }, { data: files, error: e2 }] = await Promise.all([
          _supa.from('spec_vault_folders').select('*').eq('specialist_id', specId).eq('parent_folder_id', cur.id).order('name'),
          _supa.from('spec_vault_files').select('*').eq('specialist_id', specId).eq('folder_id', cur.id).order('created_at', { ascending: false }),
        ]);
        if (e1 || e2) throw (e1 || e2);
        (subs || []).forEach(f => list.appendChild(row('📁', f.name, null, () => { nav.path = [...nav.path, { id: f.id, name: f.name }]; re(); })));
        (files || []).forEach(f => {
          const r = row('📄', f.name, _formatBytes ? _formatBytes(f.size_bytes || 0) : '', async () => {
            const { data } = await _supa.storage.from('specialist-storage').createSignedUrl(f.storage_path, 900);
            if (data?.signedUrl) window.open(data.signedUrl, '_blank', 'noopener');
          });
          const more = el('button', { class: 'cs-more', style: { background: 'none', border: 'none', fontSize: '1.05rem', cursor: 'pointer', color: 'var(--slate)', padding: '2px 6px' } }, ['⋯']);
          more.onclick = (e) => { e.stopPropagation(); fileMenu(f); };
          r.appendChild(more);
          list.appendChild(r);
        });
        if (!(subs || []).length && !(files || []).length) list.appendChild(el('div', { style: { color: 'var(--slate)', fontSize: '.8rem', padding: '6px 0' } }, [T('storage_folder_empty')]));
      }
      body.appendChild(list);

      function fileMenu(f) {
        openModal(f.name, (mb, close) => {
          mb.appendChild(mkBtn('⬇ ' + T('storage_download'), 'btn-md btn-secondary btn-full', async () => {
            const { data } = await _supa.storage.from('specialist-storage').createSignedUrl(f.storage_path, 900, { download: f.name });
            if (data?.signedUrl) { const a = document.createElement('a'); a.href = data.signedUrl; a.download = f.name; a.click(); }
            close();
          }));
          mb.appendChild(el('div', { style: { height: '6px' } }));
          mb.appendChild(_paidMark(mkBtn('✏️ ' + T('storage_rename_file'), 'btn-md btn-ghost btn-full', _paidFn('Storage', async () => {
            close(); const nn = prompt(T('storage_rename_file'), f.name);
            if (!nn || nn.trim() === f.name) return;
            const { error: e } = await _supa.from('spec_vault_files').update({ name: nn.trim() }).eq('id', f.id);
            if (e) { console.error('❌ private rename:', e); toast(T('storage_upload_failed'), 'error'); return; }
            re();
          }))));
          mb.appendChild(el('div', { style: { height: '6px' } }));
          mb.appendChild(mkBtn('🗑️ ' + T('btn_delete'), 'btn-md btn-danger btn-full', () => {
            close();
            openConfirm(T('storage_delete_file'), T('storage_delete_file_msg'), true, async () => {
              const { error: se } = await _supa.storage.from('specialist-storage').remove([f.storage_path]);
              if (se) { console.error('❌ private object delete:', se); toast(T('storage_upload_failed'), 'error'); return; }
              const { error: de } = await _supa.from('spec_vault_files').delete().eq('id', f.id);
              if (de) { console.error('❌ private file row delete:', de); toast(T('storage_upload_failed'), 'error'); return; }
              toast(T('storage_file_deleted')); re();
            });
          }));
        }, 340);
      }
    } catch (e) {
      console.error('❌ private client folder:', e);
      body.innerHTML = '';
      body.appendChild(el('div', { style: { color: 'var(--coral)', fontSize: '.8rem' } }, [T('storage_upload_failed')]));
    }
  })();
  return panel;
}

export const objectPath = M.objectPath;
export const model = M;

export function initClientStorage() {
  window.HUD_CLIENT_STORAGE = { model: M, folderIconEl, folderLabel, formerBadgeEl, prepareLevel, refuse, folderPress, deleteClientTree, treeIdFor, deleteWarningSummary, objectPath, renderPrivatePanel };
}
